import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';

type PromptTab = 'switch' | 'registry';

interface PromptItem {
  key: string;
  content: string;
}

// ── Mô tả chức năng cho từng Registry Prompt ───────────────────────────────────
const REGISTRY_DESCRIPTIONS: Record<string, string> = {
  // ―― Core Nodes ――
  'gateway_router':        'Cổng phân luồng - định tuyến yêu cầu tới đúng Lane xử lý',
  'the_brain':             'Não trung tâm - tổng hợp kết quả và trả lời cuối cùng cho user',
  'lane_0_agent':          'Lane 0 – chitchat & truyện thông thường, không cần công cụ',
  'lane_4_agent':          'Lane 4 – tác nhân ReAct tìm kiếm & lý luận sâu',
  'lane_4_medical':        'Chuyên gia y tế – tư vấn sức khỏe, dược phẩm, bệnh lý',
  'lane_4_academic':       'Nghiên cứu viên học thuật – phân tích luận văn và tài liệu khoa học',
  'lane_4_quant_investor': 'Quỹ đầu tư định lượng – phân tích thị trường, cổ phiếu, danh mục',
  'empathy_smoother':      'Lọc cảm xúc – tăng tính đồng cảm và mềm mại trong phản hồi',
  'embedder':              'Vector embedding – tạo biểu diễn ngữ nghĩa cho tìm kiếm ngữ nghĩa',
  'v2_ceo':                'V2 CEO Orchestrator – phân tích intent và lập Execution Plan',
  'v2_receptionist':       'V2 Lễ Tân – chitchat đơn giản, phân loại sơ bộ',
  'v2_expert':             'V2 Expert Node – leo thang cho bài toán phức tạp > 3 bước',
  'v2_worker':             'V2 Worker Bee – thực thi tác vụ nguyên tử song song',
  // ―― Expert Roles ――
  'CEO_EXPERT':            'Tổng giám đốc – chiến lược, quản trị, ra quyết định cấp cao',
  'executive_ceo':         'Tổng giám đốc – chiến lược, quản trị, ra quyết định cấp cao',
  'HR_EXPERT':             'Giám đốc Nhân sự – tuyển dụng, đào tạo, cấu trúc tổ chức, phúc lợi',
  'hr_management':         'Giám đốc Nhân sự – tuyển dụng, đào tạo, cấu trúc tổ chức, phúc lợi',
  'LEGAL_EXPERT':          'Giám đốc Pháp chế – hợp đồng, rủi ro pháp lý, tuân thủ pháp luật',
  'legal':                 'Giám đốc Pháp chế – hợp đồng, rủi ro pháp lý, tuân thủ pháp luật',
  'CMO_EXPERT':            'Giám đốc Marketing – thương hiệu, chiến dịch, phân tích thị trường',
  'marketing_management':  'Giám đốc Marketing – thương hiệu, chiến dịch, phân tích thị trường',
  'ACCOUNTING_EXPERT':     'Kế toán trưởng – báo cáo tài chính, chuẩn mực kế toán, thuế',
  'accounting':            'Kế toán trưởng – báo cáo tài chính, chuẩn mực kế toán, thuế',
  'FPA_EXPERT':            'Trưởng ban FP&A – dự báo, lập ngân sách, phân tích hiệu suất tài chính',
  'fpa_management':        'Trưởng ban FP&A – dự báo, lập ngân sách, phân tích hiệu suất tài chính',
  'AUDITOR_EXPERT':        'Kiểm toán nội bộ – đánh giá kiểm soát nội bộ, phát hiện gian lận',
  'financial_audit':       'Kiểm toán nội bộ – đánh giá kiểm soát nội bộ, phát hiện gian lận',
  'ADMIN_EXPERT':          'Trưởng Hành chính – văn phòng, tài sản, quy trình hành chính',
  'admin_management':      'Trưởng Hành chính – văn phòng, tài sản, quy trình hành chính',
  'BIDDING_EXPERT':        'Chuyên gia Đấu thầu – hồ sơ dự thầu, pháp lý, chiến lược giá',
  'bidding_management':    'Chuyên gia Đấu thầu – hồ sơ dự thầu, pháp lý, chiến lược giá',
  'PROCUREMENT_EXPERT':    'Trưởng Mua hàng – cước đàm phán, đánh giá NCC, tối ưu chi phí',
  'procurement_management':'Trưởng Mua hàng – cước đàm phán, đánh giá NCC, tối ưu chi phí',
  'SALES_EXPERT':          'Giám đốc Kinh doanh – chiến lược bán hàng, quản lý KH, mở rộng thị phần',
  'sales_management':      'Giám đốc Kinh doanh – chiến lược bán hàng, quản lý KH, mở rộng thị phần',
  'OPERATIONS_EXPERT':     'Giám đốc Vận hành – quy trình sản xuất, logistics, KPI vận hành',
  'operations_management': 'Giám đốc Vận hành – quy trình sản xuất, logistics, KPI vận hành',
  'RND_EXPERT':            'Giám đốc R&D – nghiên cứu, phát triển sản phẩm, đổi mới sáng tạo',
  'rd_management':         'Giám đốc R&D – nghiên cứu, phát triển sản phẩm, đổi mới sáng tạo',
  'COS_EXPERT':            'Trưởng Văn phòng (CoS) – hỗ trợ Ban lãnh đạo, điều phối chiến lược',
  'chief_of_staff':        'Trưởng Văn phòng (CoS) – hỗ trợ Ban lãnh đạo, điều phối chiến lược',
  'TA_EXPERT':             'Trưởng Tuyển dụng – sourcing, screening, đàm phán lương, onboarding',
  'talent_acquisition':    'Trưởng Tuyển dụng – sourcing, screening, đàm phán lương, onboarding',
  'INSPECTOR_EXPERT':      'Thanh tra nội bộ – kiểm tra tuân thủ, phát hiện rủi ro quy trình',
  'internal_audit':        'Thanh tra nội bộ – kiểm tra tuân thủ, phát hiện rủi ro quy trình',
  'IR_EXPERT':             'Quan hệ Nhà đầu tư – báo cáo cổ đông, thu hút vốn, ESG',
  'investor_relations':    'Quan hệ Nhà đầu tư – báo cáo cổ đông, thu hút vốn, ESG',
  'CX_EXPERT':             'Giám đốc CSKH – trải nghiệm KH, NPS, xử lý khiếu nại',
  'customer_experience':   'Giám đốc CSKH – trải nghiệm KH, NPS, xử lý khiếu nại',
  'EDU_EXPERT':            'Chuyên gia Đào tạo – thiết kế chương trình, đánh giá năng lực nhân viên',
  'education_mentor':      'Chuyên gia Đào tạo – thiết kế chương trình, đánh giá năng lực nhân viên',
  'DATA_EXPERT':           'Phân tích dữ liệu – BI, dashboard, khai thác insight từ dữ liệu',
  'data_analytics':        'Phân tích dữ liệu – BI, dashboard, khai thác insight từ dữ liệu',
  'SETUP_EXPERT':          'Chuyên gia Triển khai – chuẩn bị dự án, setup quy trình, onboarding',
  'setup_training':        'Chuyên gia Triển khai – chuẩn bị dự án, setup quy trình, onboarding',
  'document':              'Chuyên gia Soạn thảo – văn bản doanh nghiệp, hợp đồng, tờ trình',
  'DOCUMENT_EXPERT':       'Chuyên gia Soạn thảo – văn bản doanh nghiệp, hợp đồng, tờ trình',
  'PSYCHOLOGIST_PROMPT':   'Chuyên gia Tâm lý – tư vấn cảm xúc, đối thoại đồng cảm',
  'PHD_RESEARCHER_PROMPT': 'Tiến sĩ Nghiên cứu – phân tích học thuật, cite nguồn, phương pháp luận',
  // ―― Life Mastery Experts (Đời sống) ――
  'NUTRITION_WELLNESS_EXPERT': 'Chuyên gia Dinh dưỡng & Sức khỏe – Macro, TDEE, tiểu đường, gout, giấc ngủ',
  'VET_PET_EXPERT':            'Bác sĩ Thú y – Cấp cứu, sơ cứu ngộ độc, dinh dưỡng & hành vi thú cưng',
  'PARENTING_CHILD_EXPERT':    'Chuyên gia Nuôi dạy con – Kỷ luật tích cực, tâm lý trẻ, cai game/màn hình',
  'RELATIONSHIP_FAMILY_EXPERT':'Cố vấn Hôn nhân & Gia đình – Gottman, NVC, mẹ chồng nàng dâu, hòa giải',
  'PERSONAL_FINANCE_EXPERT':   'Tài chính Cá nhân & Gia đình – Mua/thuê nhà, ngân sách 50/30/20, bảo hiểm, xóa nợ',
  'LIFE_HABIT_COACH':          'Huấn luyện viên Thói quen – Atomic habits, quản trị năng lượng, vượt trì hoãn',
  'CIVIL_LEGAL_EXPERT':        'Luật sư Dân sinh – Sổ đỏ, thừa kế, quyền nuôi con, vi bằng, bẫy hợp đồng',
  'SOCIAL_ETIQUETTE_EXPERT':   'Văn hóa Ứng xử & Đối nhân xử thế – Cưới hỏi/ma chay, từ chối vay tiền, EQ Á Đông',
  // Switch prompts
  'collaboration':         'Lăng kính hợp tác – đưa ra phán đoán kết hợp đa góc nhìn',
  'data_processing':       'Lăng kính xử lý dữ liệu – chỉ trả về dữ liệu cấu trúc, bảng, số',
  'philosophy_mindset':    'Lăng kính triết học – lý luận sâu, đa chiều, phản biện',
  'presentation':          'Lăng kính thuyết trình – sắp xếp nội dung rõ ràng, động nước',
};

// Normalize text: loại bỏ dấu tiếng Việt, ký tự đặc biệt, underscore, chuyển thường
function normalizeSearch(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')  // bỏ dấu tiếng Việt
    .replace(/[^a-z0-9\s]/g, ' ')    // bỏ ký tự đặc biệt, underscore → space
    .replace(/\s+/g, ' ')             // cất nhiều space
    .trim();
}

export function SwarmV2Page() {
  const [msg, setMsg] = useState('');

  // ── Prompt Management ──────────────────────────────────────────
  const [promptTab, setPromptTab] = useState<PromptTab>('switch');
  const [switchPrompts, setSwitchPrompts] = useState<PromptItem[]>([]);
  const [registryPrompts, setRegistryPrompts] = useState<PromptItem[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [editorContent, setEditorContent] = useState('');
  const [editorKey, setEditorKey] = useState(''); // key name khi thêm mới
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [coreUnlocked, setCoreUnlocked] = useState(false);

  // Core system keys to protect
  const isCoreKey = (k: string) => {
    return k.startsWith('v2_ceo') || k.startsWith('v2_receptionist') || 
           k.startsWith('v2_worker') || k.startsWith('v2_expert') || k === 'gateway_router' || 
           k === 'the_brain' || k.includes('lane') || k.includes('node') || 
           k.includes('embedder') || k.includes('smoother');
  };

  const [seeding, setSeeding] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [migrating, setMigrating] = useState(false);

  const flash = useCallback((text: string) => {
    setMsg(text);
    setTimeout(() => setMsg(''), 3000);
  }, []);

  // Load prompts
  useEffect(() => {
    loadPrompts();
  }, []);

  const seedDefaults = async () => {
    setSeeding(true);
    try {
      const result = await api.v2SeedDefaults();
      flash(`✓ Đã khởi tạo ${result.seeded} prompts mặc định`);
      await loadPrompts();
    } catch (e) {
      flash('✗ ' + String(e));
    } finally {
      setSeeding(false);
    }
  };

  // [LEGACY] kept for backward compat — not shown in UI anymore
  const syncFromV1 = async () => {
    setSyncing(true);
    try {
      const result = await api.v2SyncV1();
      flash(`✓ Đã đồng bộ ${result.synced} prompts từ V1 Nodes`);
      await loadPrompts();
    } catch (e) {
      flash('✗ ' + String(e));
    } finally {
      setSyncing(false);
    }
  };

  // Hợp nhất toàn bộ Expert Prompts vào v2_registry: (kiến trúc mới)
  const migrateToUnified = async () => {
    if (!confirm('⚠️ Chạy Migration một lần: Nạp toàn bộ Expert Prompt từ file template vào kho v2_registry:\n\n• Các Prompt đã có trong KV sẽ ĐUỢC GIỮ NGUYÊN (không ghi đè)\n• Chỉ nạp các Prompt chưa tồn tại\n\nTiếp tục?')) return;
    setMigrating(true);
    try {
      const res = await fetch('/api/admin/v2/migrate-to-unified', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('eric_ai_token') || ''}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Migration failed');
      flash(`✓ Migration xong! Expert nạp mới: ${data.expert_prompts_seeded} | Core migrate: ${data.core_prompts_migrated} | Tổng registry: ${data.total_expert_keys}`);
      await loadPrompts();
    } catch (e) {
      flash('✗ ' + String(e));
    } finally {
      setMigrating(false);
    }
  };

  const loadPrompts = async () => {
    try {
      const [sw, rg] = await Promise.all([
        api.v2ListSwitchPrompts(),
        api.v2ListRegistryPrompts(),
      ]);
      setSwitchPrompts(sw.items ?? []);
      setRegistryPrompts(rg.items ?? []);
    } catch (e) {
      flash('⚠ Không tải được prompts: ' + String(e));
    }
  };


  const currentPrompts = promptTab === 'switch' ? switchPrompts : registryPrompts;
  const filtered = currentPrompts.filter(p => {
    if (!searchQ) return true;
    const q = normalizeSearch(searchQ);
    const inKey = normalizeSearch(p.key).includes(q);
    const inDesc = normalizeSearch(REGISTRY_DESCRIPTIONS[p.key] ?? '').includes(q);
    const inContent = normalizeSearch(p.content.slice(0, 200)).includes(q);
    return inKey || inDesc || inContent;
  });

  const selectPrompt = (item: PromptItem) => {
    setSelectedKey(item.key);
    setEditorKey(item.key);
    setEditorContent(item.content);
    setIsAddingNew(false);
  };

  const startAddNew = () => {
    setSelectedKey(null);
    setEditorKey('');
    setEditorContent('');
    setIsAddingNew(true);
  };

  const savePrompt = async () => {
    const key = isAddingNew ? editorKey.trim() : selectedKey!;
    if (!key) { flash('✗ Vui lòng nhập tên key'); return; }
    if (!editorContent.trim()) { flash('✗ Nội dung không được trống'); return; }
    setSaving(true);
    try {
      if (promptTab === 'switch') {
        await api.v2SaveSwitchPrompt(key, editorContent);
      } else {
        await api.v2SaveRegistryPrompt(key, editorContent);
      }
      flash(`✓ Đã lưu "${key}"`);
      await loadPrompts();
      if (isAddingNew) {
        setSelectedKey(key);
        setIsAddingNew(false);
      }
    } catch (e) {
      flash('✗ ' + String(e));
    } finally {
      setSaving(false);
    }
  };

  const deletePrompt = async (key: string) => {
    if (!confirm(`Xoá prompt "${key}"?`)) return;
    try {
      if (promptTab === 'switch') await api.v2DeleteSwitchPrompt(key);
      else await api.v2DeleteRegistryPrompt(key);
      flash(`✓ Đã xoá "${key}"`);
      if (selectedKey === key) { setSelectedKey(null); setEditorContent(''); setEditorKey(''); }
      await loadPrompts();
    } catch (e) {
      flash('✗ ' + String(e));
    }
  };

  return (
    <div>
      {/* ── HEADER ── */}
      <div className="page-header" style={{ flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 className="page-title">⚡ Unified Hybrid Swarm Control Panel</h1>
          <p className="page-subtitle">
            Hệ thống Swarm Hợp nhất (V1 + V2): Multi-Lane Router kết hợp CEO &amp; C-Level Multi-Agent Engine
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {msg && (
            <span style={{ fontSize: '13px', color: msg.startsWith('✓') ? '#10b981' : '#ef4444' }}>
              {msg}
            </span>
          )}
          {/* Badges: Unified Architecture + 5-Tier Cache + Erix MEC */}
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              fontSize: '12px', padding: '5px 12px', borderRadius: '9999px',
              background: 'linear-gradient(90deg, rgba(16,185,129,0.15), rgba(139,92,246,0.15))',
              color: '#10b981',
              border: '1px solid rgba(16,185,129,0.4)',
              fontWeight: 600,
            }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981', display: 'inline-block', boxShadow: '0 0 8px #10b981' }}></span>
              🟢 UNIFIED SWARM V1+V2
            </span>

            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              fontSize: '12px', padding: '5px 12px', borderRadius: '9999px',
              background: 'rgba(56,189,248,0.12)',
              color: '#38bdf8',
              border: '1px solid rgba(56,189,248,0.3)',
              fontWeight: 600,
            }} title="L1 Exact Hash KV (<10ms) • L2 Vector Cache (BGE-M3 7d) • L3 Semantic Jaccard • L4 Prefix Freeze • L5 Swarm Blackboard">
              ⚡ 5-TIER CACHE PYRAMID
            </span>

            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              fontSize: '12px', padding: '5px 12px', borderRadius: '9999px',
              background: 'rgba(168,85,247,0.12)',
              color: '#c084fc',
              border: '1px solid rgba(168,85,247,0.3)',
              fontWeight: 600,
            }} title="MCP JSON-RPC 2.0 / SSE • REST Fallback • Zero-Knowledge PII Masking • Single-Pass Vectorization">
              🧠 ERIX MEC HYBRID ACTIVE
            </span>
          </div>
        </div>
      </div>

      {/* ── PHÂN VÙNG I: PROMPT MANAGEMENT ── */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ fontSize: '18px' }}>📚</div>
            <div className="card__title" style={{ margin: 0 }}>Phân Vùng I — Quản Lý Prompt</div>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              {switchPrompts.length} switch · {registryPrompts.length} registry
            </span>
          </div>
          <button
            id="btn-migrate-unified"
            className="btn btn--primary btn--sm"
            onClick={migrateToUnified}
            disabled={migrating}
            title="Hợp nhất toàn bộ Expert Prompts từ template files vào kho v2_registry: (chạy 1 lần)"
            style={{ fontSize: '11px', gap: '4px' }}
          >
            {migrating ? <span className="spinner" style={{ width: 10, height: 10 }} /> : '🔗'}
            Migrate về Kho Chung
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: '16px', minHeight: '500px' }}>
          {/* Left: list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Tabs */}
            <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
              {([['switch', '🧭 Switch Prompts'], ['registry', '🎭 Registry Prompts']] as const).map(([tab, label]) => (
                <button
                  key={tab}
                  id={`tab-${tab}`}
                  onClick={() => { setPromptTab(tab); setSelectedKey(null); setEditorContent(''); setIsAddingNew(false); setSearchQ(''); }}
                  style={{
                    flex: 1, padding: '7px 4px', borderRadius: '8px', border: 'none',
                    cursor: 'pointer', fontSize: '11px', fontWeight: 600,
                    background: promptTab === tab ? 'var(--accent-primary)' : 'var(--bg-elevated)',
                    color: promptTab === tab ? '#fff' : 'var(--text-secondary)',
                    transition: 'all 0.2s',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Search */}
            <input
              id="prompt-search"
              className="input"
              placeholder="🔍 Tìm kiếm..."
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              style={{ fontSize: '12px' }}
            />

            {/* List */}
            <div className="custom-scrollbar" style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: 'calc(100vh - 260px)', paddingRight: '4px' }}>
              {filtered.length === 0 && !searchQ && (
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center', padding: '16px 8px', display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
                  <span>Chưa có prompt nào</span>
                  <button
                    id="btn-seed-defaults"
                    className="btn btn--primary btn--sm"
                    onClick={seedDefaults}
                    disabled={seeding}
                    style={{ fontSize: '11px' }}
                  >
                    {seeding ? <span className="spinner" style={{ width: 10, height: 10 }} /> : '🌱 Khởi tạo mặc định'}
                  </button>
                </div>
              )}
              {filtered.length === 0 && searchQ && (
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center', padding: '16px' }}>Không tìm thấy</div>
              )}
              {(() => {
                const sortedFiltered = [...filtered].sort((a, b) => a.key.localeCompare(b.key));
                
                if (promptTab === 'registry') {
                  const groups = [
                    { 
                      name: 'Kiến Trúc Lõi Hệ Thống', 
                      desc: 'Các Registry Prompt của luồng vận hành lõi', 
                      items: [] as typeof sortedFiltered, 
                      filter: (k: string) => isCoreKey(k) 
                    },
                    { 
                      name: 'Tất cả Chuyên Gia Mở Rộng', 
                      desc: '', 
                      items: [] as typeof sortedFiltered, 
                      filter: (k: string) => !isCoreKey(k) 
                    }
                  ];

                  sortedFiltered.forEach(item => {
                    const k = item.key.toLowerCase();
                    if (isCoreKey(k)) groups[0].items.push(item);
                    else groups[1].items.push(item);
                  });

                  return groups.map(g => {
                    const isCoreGroup = g.name === 'Kiến Trúc Lõi Hệ Thống';
                    if (g.items.length === 0 && !isCoreGroup) return null; // Luôn hiện header core để thấy nút lock

                    return (
                      <div key={g.name} style={{ marginBottom: '24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', borderBottom: '1px solid var(--border-color)', paddingBottom: '4px' }}>
                          <div>
                            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)', textTransform: 'uppercase' }}>
                              {g.name}
                            </div>
                            {g.desc && (
                              <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontStyle: 'italic', marginTop: '2px' }}>
                                {isCoreGroup && !coreUnlocked ? 'Các Registry Prompt của luồng vận hành lõi đang bị khóa để tránh sửa nhầm.' : g.desc}
                              </div>
                            )}
                          </div>
                          {isCoreGroup && (
                            <button 
                              type="button" 
                              onClick={() => setCoreUnlocked(!coreUnlocked)} 
                              style={{ fontSize: '10px', background: coreUnlocked ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.1)', border: 'none', color: coreUnlocked ? '#f87171' : 'var(--text-muted)', cursor: 'pointer', padding: '4px 8px', borderRadius: '4px', fontWeight: 600 }}
                            >
                              {coreUnlocked ? '🔓 Đã mở khóa' : '🔒 Khóa an toàn'}
                            </button>
                          )}
                        </div>

                        {(!isCoreGroup || coreUnlocked) && g.items.map(item => {
                          const isSub = item.key.includes('::');
                          const displayName = isSub ? '↳ ' + item.key.split('::')[1] : item.key;
                          return (
                            <div
                              key={item.key}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '6px',
                                padding: '8px 10px', borderRadius: '8px', cursor: 'pointer',
                                background: selectedKey === item.key ? 'rgba(139,92,246,0.15)' : 'var(--bg-elevated)',
                                border: `1px solid ${selectedKey === item.key ? 'rgba(139,92,246,0.4)' : 'transparent'}`,
                                transition: 'all 0.15s',
                                marginLeft: isSub ? '16px' : '0',
                                opacity: isSub ? 0.8 : 1,
                                marginTop: '4px'
                              }}
                              onClick={() => selectPrompt(item)}
                            >
                              <span style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-primary)', wordBreak: 'break-all', lineHeight: '1.3' }}>
                                  {displayName}
                                </div>
                                {REGISTRY_DESCRIPTIONS[item.key] && (
                                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '3px', lineHeight: '1.4', wordBreak: 'break-word' }}>
                                    {REGISTRY_DESCRIPTIONS[item.key]}
                                  </div>
                                )}
                              </span>
                              {!isSub && (
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    startAddNew();
                                    setEditorKey(item.key + '::');
                                    // wait for react state to render input then focus
                                    setTimeout(() => document.getElementById('new-prompt-key')?.focus(), 100);
                                  }}
                                  title="Add Sub Registry"
                                  style={{ background: 'rgba(16,185,129,0.1)', border: 'none', cursor: 'pointer', fontSize: '10px', padding: '2px 6px', color: '#10b981', fontWeight: 600, borderRadius: '4px' }}
                                >+ Sub</button>
                              )}
                              <button
                                id={`edit-${item.key}`}
                                onClick={e => { e.stopPropagation(); selectPrompt(item); }}
                                title="Sửa"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', padding: '2px', color: 'var(--text-muted)' }}
                              >✏️</button>
                              <button
                                id={`delete-${item.key}`}
                                onClick={e => { e.stopPropagation(); deletePrompt(item.key); }}
                                title="Xoá"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', padding: '2px', color: '#ef4444' }}
                              >✕</button>
                            </div>
                          );
                        })}
                        {isCoreGroup && !coreUnlocked && (
                          <div style={{ padding: '16px', textAlign: 'center', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', border: '1px dashed rgba(255,255,255,0.05)' }}>
                            <div style={{ fontSize: '24px', marginBottom: '8px', opacity: 0.5 }}>🔒</div>
                            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Vui lòng mở khóa để chỉnh sửa Prompt Lõi</div>
                          </div>
                        )}
                      </div>
                    );
                  });
                }

                // Switch prompt render mapping
                return sortedFiltered.map(item => {
                  return (
                    <div
                      key={item.key}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        padding: '8px 10px', borderRadius: '8px', cursor: 'pointer',
                        background: selectedKey === item.key ? 'rgba(139,92,246,0.15)' : 'var(--bg-elevated)',
                        border: `1px solid ${selectedKey === item.key ? 'rgba(139,92,246,0.4)' : 'transparent'}`,
                        transition: 'all 0.15s',
                        marginTop: '4px'
                      }}
                      onClick={() => selectPrompt(item)}
                    >
                      <span style={{ flex: 1, fontSize: '12px', fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.key}
                      </span>
                      <button
                        id={`edit-${item.key}`}
                        onClick={e => { e.stopPropagation(); selectPrompt(item); }}
                        title="Sửa"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', padding: '2px', color: 'var(--text-muted)' }}
                      >✏️</button>
                      <button
                        id={`delete-${item.key}`}
                        onClick={e => { e.stopPropagation(); deletePrompt(item.key); }}
                        title="Xoá"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', padding: '2px', color: '#ef4444' }}
                      >✕</button>
                    </div>
                  );
                });
              })()}
            </div>

            <button
              id="btn-add-new-prompt"
              className="btn btn--secondary"
              onClick={startAddNew}
              style={{ justifyContent: 'center', fontSize: '12px' }}
            >
              + Thêm mới
            </button>
          </div>

          {/* Right: editor */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {(selectedKey || isAddingNew) ? (
              <>
                {isAddingNew && (
                  <input
                    id="new-prompt-key"
                    className="input"
                    placeholder="Tên key (ví dụ: crypto_trader, legal_expert...)"
                    value={editorKey}
                    onChange={e => setEditorKey(e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                    style={{ fontFamily: 'var(--font-mono)', fontSize: '13px' }}
                  />
                )}
                {!isAddingNew && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <code style={{ fontSize: '13px', color: '#a78bfa', fontFamily: 'var(--font-mono)' }}>{selectedKey}</code>
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                      {promptTab === 'switch' ? '— Kim chỉ nam' : '— Nhập vai chuyên gia'}
                    </span>
                  </div>
                )}
                <textarea
                  id="prompt-editor"
                  className="textarea"
                  style={{ flex: 1, minHeight: '360px', fontSize: '12px', lineHeight: 1.6, fontFamily: 'var(--font-mono)' }}
                  value={editorContent}
                  onChange={e => setEditorContent(e.target.value)}
                  spellCheck={false}
                  placeholder="Nhập nội dung prompt..."
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                    {editorContent.length} ký tự · ~{Math.ceil(editorContent.length / 4)} tokens
                  </span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      className="btn btn--secondary btn--sm"
                      onClick={() => { setSelectedKey(null); setEditorContent(''); setIsAddingNew(false); }}
                    >
                      Huỷ
                    </button>
                    <button
                      id="btn-save-prompt"
                      className="btn btn--primary btn--sm"
                      onClick={savePrompt}
                      disabled={saving}
                    >
                      {saving ? <span className="spinner" style={{ width: 12, height: 12 }} /> : '💾 Lưu'}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div style={{
                flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', color: 'var(--text-muted)', gap: '12px',
              }}>
                <div style={{ fontSize: '48px', opacity: 0.3 }}>📝</div>
                <div style={{ fontSize: '13px' }}>Chọn một prompt từ danh sách hoặc tạo mới</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── ĐIỀU HƯỚNG CẤU HÌNH AI NODES ── */}
      <div
        className="card"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '18px 24px',
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(59, 130, 246, 0.05) 100%)',
          border: '1px solid rgba(139, 92, 246, 0.25)',
          borderRadius: '16px',
          flexWrap: 'wrap',
          gap: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{ fontSize: '28px' }}>🤖</div>
          <div>
            <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-light)' }}>
              Cấu hình mô hình AI Nodes Swarm V2
            </div>
            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '3px' }}>
              Quản lý toàn diện Provider Chain (Cloudflare, DeepSeek, OpenAI, Gemini...), System Prompt và gán Prompt mặc định tại trang trung tâm.
            </div>
          </div>
        </div>
        <Link
          to="/nodes"
          className="btn btn--primary btn--sm"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            textDecoration: 'none',
            padding: '8px 16px',
            fontSize: '13px',
            fontWeight: 600,
          }}
        >
          <span>Thiết lập tại AI Nodes</span>
          <span>→</span>
        </Link>
      </div>
    </div>
  );
}
